import { EditCheckoutRequest } from '@/@types/Checkouts/create-checkout';
import { Checkout } from '@/@types/Checkouts/list-checkouts';
import { AxiosErrorResponse } from '@/@types/Errors';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { Input } from '@/components/shared/Input/Input';
import { Button } from '@/components/ui/button';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { CheckoutsService } from '@/services/checkouts.service';
import useModalStore from '@/store/useModalStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Eye, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { normalizeUrlWhenPaste } from '@/utils/normalize';

interface CreateCheckoutProps {
  offers: {
    value: string;
    label: string;
  }[];
  checkout: Checkout;
  modalName: string;
}

export default function EditCheckout({
  offers,
  checkout,
  modalName,
}: CreateCheckoutProps) {
  const { t } = useTranslation(['checkouts', 'common']);
  const { closeModal } = useModalStore();
  const { id } = useParams();

  const CreateOfferSchema = z.object({
    name: z.string().min(1, t('common:required_field')),
    offer_id: z.string().min(1, t('common:required_field')),
    back_redirect: z
      .string()
      .optional()
      .refine((value) => !value || /^https?:\/\/.+$/.test(value), {
        message: t('common:invalid_url'),
      }),
    is_active: z.boolean(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<EditCheckoutRequest>({
    shouldUnregister: false,
    resolver: zodResolver(CreateOfferSchema),
    defaultValues: {
      name: checkout?.name ?? '',
      offer_id: checkout?.offer.id ?? '0',
      is_active: checkout?.is_active ?? false,
      back_redirect: checkout?.back_redirect ?? '',
    },
  });

  const defaultValues = watch();
  const namePreview =
    defaultValues?.name?.trim() || t('checkouts:name_preview_placeholder');

  const queryClient = useQueryClient();

  const { mutate, isPending: isCreatingCheckout } = useMutation({
    mutationFn: (data: EditCheckoutRequest) =>
      CheckoutsService.updateCheckout({
        data,
        checkoutId: String(checkout.id),
        productId: id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`checkouts-${id}`] });
      queryClient.invalidateQueries({ queryKey: ['checkouts', id] });
      queryClient.invalidateQueries({ queryKey: ['public-marketplace', id] });
      toast({
        variant: 'success',
        description: t('checkouts:saved_checkout'),
      });
      closeModal(modalName);
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: 'error',
        description:
          error?.response.data.message || 'Ocorreu um erro ao enviar os dados.',
      });
    },
  });

  const onSubmit = async (data: EditCheckoutRequest) => {
    mutate(data);
  };

  return (
    <section
      className='sm:overflow-y-hidden flex flex-col justify-between h-full'
      data-cy='modal-edit-checkout'
    >
      <SheetHeader className='p-6'>
        <SheetTitle className='flex gap-2'>
          {t('common:edit_checkout')}
        </SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-col justify-between h-full'
      >
        <section className='flex flex-col gap-4 px-6'>
          <div className='flex gap-2 items-center'>
            <Switch
              checked={defaultValues?.is_active}
              onCheckedChange={(value) => setValue('is_active', value)}
              data-cy='switch-checkout-active'
            />
            <Label>{t('common:activate_checkout')}</Label>
          </div>
          <div className='space-y-2'>
            <Input
              {...register('name')}
              label={t('common:name_single')}
              placeholder={t('checkouts:enter_checkout_name')}
              data-cy='input-checkout-name'
              helperText={errors.name?.message}
            />
            <p className='text-sm text-muted'>{t('checkouts:name_helper')}</p>
            <div className='flex items-start gap-2 rounded-md border border-colorBorder bg-[#09090B] px-3 py-2.5'>
              <Eye className='mt-0.5 h-4 w-4 shrink-0 text-muted' />
              <p className='text-sm leading-relaxed'>
                <span className='text-muted'>
                  {t('common:offerNameCheckoutPreview')}
                </span>{' '}
                <span className='font-medium text-interactive'>{namePreview}</span>
              </p>
            </div>
          </div>
          <CustomSelect
            options={offers}
            label={t('common:offer')}
            placeholder={t('common:select_offer')}
            dataCy='select-checkout-offer'
            onValueChange={(value) => {
              setValue('offer_id', value as any);
              clearErrors('offer_id');
            }}
            value={defaultValues?.offer_id}
            helperText={errors.offer_id?.message}
          />
          <Input
            {...register('back_redirect')}
            label={t('Back Redirect')}
            placeholder={t('checkouts:enter_back_redirect')}
            data-cy='input-checkout-back-redirect'
            helperText={errors.back_redirect?.message}
            onFocus={(e) => {
              if (!e.target.value) {
                e.target.value = 'https://';
                setValue('back_redirect', 'https://');
              }
            }}
            onPaste={(e) => {
              const finalValue = normalizeUrlWhenPaste(e);
              setValue('back_redirect', finalValue);
            }}
          />
        </section>
        <div className='flex border-t items-center justify-end gap-4 px-6 py-4 w-full max-sm:pb-12'>
          <Button
            variant='ghost'
            onClick={() => closeModal(modalName)}
            type='button'
          >
            {t('common:cancel')}
          </Button>
          <Button
            disabled={isCreatingCheckout}
            type='submit'
            data-cy='btn-save-checkout'
          >
            {isCreatingCheckout ? (
              <span className='flex items-center gap-2'>
                <Loader2 className='animate-spin' />
                {t('common:saving')}
              </span>
            ) : (
              t('common:save_changes')
            )}
          </Button>
        </div>
      </form>
    </section>
  );
}
